/* eslint-disable @typescript-eslint/no-explicit-any */

import { getClientLogger } from '@/lib/logger/pino-logger.client';
import { IFormCaptchaInput, IFormCaptchaResponse } from '@/types/forms';
import { useState } from 'react';
import { useRequest } from './useRequest';

const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const useRecaptcha = () => {
  const logger = getClientLogger('hooks.useRecaptcha');

  const [localError, setLocalError] = useState<boolean>(false);
  const { hasError, isLoading, sendRequest } = useRequest<IFormCaptchaResponse, IFormCaptchaInput>(
    '/api/forms/validateCaptcha',
    { logger },
  );

  const installRecaptcha = async (siteKey: string) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const firstElement = (document as any).getElementsByTagName('script')[0];
    const recaptchaElement = (document as any).createElement('script');
    recaptchaElement.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    firstElement.parentNode.insertBefore(recaptchaElement, firstElement);

    while (!(window as any)?.grecaptcha?.enterprise) {
      console.log('Waiting for reCAPTCHA to load...');
      await sleep(1000);
    }

    console.log('reCAPTCHA loaded successfully');
  };

  const getActionSanitized = (action: string) => {
    return action
      ? action
          .toLowerCase()
          .replace(/[^a-zA-Z0-9-_\s]/g, '') // Prevent non-standard characters
          .replace(/[-\s]/g, '_') // reCaptcha recommends underscores
      : Date.now().toString();
  };

  const getToken = async (siteKey: string, action: string) => {
    try {
      return await (window as any).grecaptcha.enterprise.execute(siteKey, {
        action,
      });
    } catch (error) {
      logger.error(`Error executing reCAPTCHA: ${(error as Error).message}`);
      setLocalError(true);
    }
  };

  const verifyCaptcha = async (siteKey: string, action: string) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    if (!(window as any)?.grecaptcha?.enterprise) {
      console.error('reCAPTCHA is not loaded');
      await installRecaptcha(siteKey);
    }

    const actionSanitized = getActionSanitized(action);
    const token = await getToken(siteKey, actionSanitized);

    if (!token) {
      setLocalError(true);
      return {
        success: false,
        error: 'Captcha token is required',
      };
    }

    return sendRequest({ token, action: actionSanitized });
  };

  return {
    hasError: localError || hasError,
    isLoading,
    verifyCaptcha,
  };
};
